import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Wrench } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FieldDef {
  name: string;
  type?: string;
  optional?: boolean;
}

export interface StepNodeData {
  stepIndex: number;
  label: string;
  description?: string;
  inputFields: FieldDef[];
  outputFields: FieldDef[];
  tools?: string[];
  color?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Step accent colors (cycles by index)
// ---------------------------------------------------------------------------

export const STEP_COLORS = [
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#06B6D4', // cyan
  '#EC4899', // pink
];

// ---------------------------------------------------------------------------
// Field type chip
// ---------------------------------------------------------------------------

const TYPE_STYLES: Record<string, string> = {
  string:  'text-sky-400',
  number:  'text-amber-400',
  boolean: 'text-green-400',
  array:   'text-purple-400',
  object:  'text-orange-400',
  integer: 'text-amber-400',
};

function TypeChip({ type, optional }: { type?: string; optional?: boolean }) {
  if (!type) return null;
  const cls = TYPE_STYLES[type] ?? 'text-slate-400';
  return (
    <span className={`font-mono text-[9px] ${cls} shrink-0`}>
      {type}{optional ? '?' : ''}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Field list panel
// ---------------------------------------------------------------------------

function FieldPanel({
  title,
  fields,
  side,
}: {
  title: string;
  fields: FieldDef[];
  side: 'input' | 'output';
}) {
  const isInput = side === 'input';

  return (
    <div
      className="flex-1 p-3 min-w-0"
      style={{
        background: isInput
          ? 'rgba(16, 185, 129, 0.05)'
          : 'rgba(139, 92, 246, 0.05)',
        borderTop: '1px solid rgba(51,65,85,0.6)',
      }}
    >
      {/* Panel label */}
      <div
        className="text-[9px] uppercase tracking-widest font-semibold mb-2"
        style={{ color: isInput ? '#34d399' : '#a78bfa' }}
      >
        {title}
      </div>

      {/* Fields */}
      {fields.length === 0 ? (
        <span className="text-[10px] italic text-slate-600">—</span>
      ) : (
        <div className="space-y-1">
          {fields.map((f) => (
            <div key={f.name} className="flex items-center gap-1.5 leading-none">
              <span
                className="w-1 h-1 rounded-full shrink-0"
                style={{ background: isInput ? '#34d399' : '#a78bfa', opacity: 0.5 }}
              />
              <span className="text-[11px] text-slate-200 font-mono truncate">{f.name}</span>
              <TypeChip type={f.type} optional={f.optional} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main node
// ---------------------------------------------------------------------------

function WorkflowStepNodeComponent({ data }: NodeProps) {
  const {
    stepIndex,
    label,
    description,
    inputFields,
    outputFields,
    tools,
    color = STEP_COLORS[0],
  } = data as StepNodeData;

  return (
    <div
      className="rounded-xl border border-slate-700/80 bg-slate-900 shadow-xl overflow-hidden"
      style={{
        width: 440,
        borderLeft: `4px solid ${color}`,
      }}
    >
      {/* Target handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2.5 !h-2.5 !border-2 !border-slate-900"
        style={{ background: color, left: -6 }}
      />

      {/* Header */}
      <div className="px-4 pt-3 pb-2.5">
        <div className="flex items-center gap-2.5 mb-1.5">
          {/* Step number badge */}
          <span
            className="text-[10px] font-bold rounded px-1.5 py-0.5 shrink-0"
            style={{ background: `${color}22`, color }}
          >
            {String(stepIndex).padStart(2, '0')}
          </span>
          {/* Step name */}
          <span className="text-[13px] font-semibold text-white leading-tight truncate">
            {label}
          </span>
        </div>

        {/* Description */}
        {description ? (
          <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-3 pl-[30px]">
            {description}
          </p>
        ) : (
          <p className="text-[11px] text-slate-600 italic pl-[30px]">No description</p>
        )}

        {/* Tool badges */}
        {tools && tools.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 pl-[30px]">
            {tools.map((tool) => (
              <span
                key={tool}
                className="inline-flex items-center gap-0.5 text-[9px] bg-slate-800 border border-slate-700 text-amber-400 px-1.5 py-0.5 rounded-full"
              >
                <Wrench className="h-2 w-2" />
                {tool}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Schema panels */}
      <div className="flex divide-x divide-slate-700/60">
        <FieldPanel title="↳ Inputs" fields={inputFields} side="input" />
        <FieldPanel title="Outputs ↴" fields={outputFields} side="output" />
      </div>

      {/* Source handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-2.5 !h-2.5 !border-2 !border-slate-900"
        style={{ background: color, right: -6 }}
      />
    </div>
  );
}

export const WorkflowStepNode = memo(WorkflowStepNodeComponent);
